import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import theme from '../theme';
import {
  ARCHIVE_API_PATH,
  REORDER_API_PATH,
  AchievementRendererCard,
  buildPublicRows,
  publicShapeClass,
} from './achievements/shared';

function idsOf(items) {
  return items.map((item) => item.id);
}

function EditorCard({ item, isDragging, isTarget, onDragStart, onDragEnter, onDragEnd }) {
  return (
    <div
      draggable
      onDragStart={() => onDragStart(item.id)}
      onDragEnter={() => onDragEnter(item.id)}
      onDragOver={(event) => event.preventDefault()}
      onDragEnd={onDragEnd}
      className={`achievement-public-card is-editable ${publicShapeClass(item)} ${isDragging ? 'is-dragging' : ''} ${isTarget ? 'is-target' : ''}`}
    >
      <div className="achievement-public-card-frame">
        <AchievementRendererCard record={item} displayMode="board" />
      </div>
    </div>
  );
}

function BoardEditorPage() {
  const { user } = useContext(AuthContext);
  const [sequence, setSequence] = useState([]);
  const [initialIds, setInitialIds] = useState([]);
  const [draggingId, setDraggingId] = useState(null);
  const [targetId, setTargetId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!user) {
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);
    setError('');

    async function loadArchive() {
      try {
        const response = await fetch(ARCHIVE_API_PATH);
        if (!response.ok) {
          throw new Error('Cards unavailable');
        }
        const payload = await response.json();
        const published = (Array.isArray(payload.items) ? payload.items : []).filter((item) => item.isPublished);
        if (isMounted) {
          setSequence(published);
          setInitialIds(idsOf(published));
        }
      } catch (loadError) {
        if (isMounted) {
          setSequence([]);
          setError('Cards unavailable.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadArchive();

    return () => {
      isMounted = false;
    };
  }, [user]);

  const isDirty = useMemo(() => {
    const current = idsOf(sequence);
    return current.length !== initialIds.length || current.some((id, index) => id !== initialIds[index]);
  }, [sequence, initialIds]);

  const rows = useMemo(() => buildPublicRows(sequence), [sequence]);

  if (!user) {
    return <Redirect to="/achievements" />;
  }

  const handleDragStart = (id) => {
    setDraggingId(id);
    setJustSaved(false);
  };

  const handleDragEnter = (id) => {
    setTargetId(id);
    if (draggingId === null || draggingId === id) {
      return;
    }
    setSequence((current) => {
      const from = current.findIndex((item) => item.id === draggingId);
      const to = current.findIndex((item) => item.id === id);
      if (from === -1 || to === -1 || from === to) {
        return current;
      }
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setTargetId(null);
  };

  const saveLayout = async () => {
    setIsSaving(true);
    setError('');

    try {
      const response = await fetch(REORDER_API_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: idsOf(sequence) }),
      });
      if (!response.ok) {
        throw new Error('Save failed');
      }
      const payload = await response.json();
      const published = (Array.isArray(payload.items) ? payload.items : []).filter((item) => item.isPublished);
      if (isMountedRef.current) {
        setSequence(published);
        setInitialIds(idsOf(published));
        setJustSaved(true);
      }
    } catch (saveError) {
      if (isMountedRef.current) {
        setError('Save failed.');
      }
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  };

  return (
    <section className="space-y-6">
      <div className="rounded-lg border border-olive-200 bg-gradient-to-br from-white via-olive-50 to-sky-50 p-6 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className={`text-sm font-semibold uppercase ${theme.textMuted}`}>Achievements</p>
            <h1 className={`mt-2 text-4xl font-bold ${theme.textPrimary}`}>Edit the board</h1>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href="/achievements"
              className="inline-flex items-center justify-center rounded-lg border border-olive-200 bg-white px-5 py-3 text-sm font-semibold text-olive-700 shadow-sm hover:bg-olive-50"
            >
              Back
            </a>
            <button
              type="button"
              onClick={saveLayout}
              disabled={!isDirty || isSaving}
              className={`inline-flex items-center justify-center rounded-lg px-5 py-3 text-sm font-semibold shadow-sm disabled:cursor-not-allowed disabled:bg-olive-100 disabled:text-olive-400 ${theme.btnPrimary}`}
            >
              {isSaving ? 'Saving' : 'Save layout'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
          {error}
        </div>
      )}

      {justSaved && !isDirty && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
          Layout saved.
        </div>
      )}

      {isLoading ? (
        <div className="rounded-lg border border-olive-200 bg-white p-10 text-center shadow-sm">
          <p className={`text-sm font-bold uppercase ${theme.textMuted}`}>Loading</p>
        </div>
      ) : sequence.length === 0 ? (
        <div className="rounded-lg border border-dashed border-olive-200 bg-white p-10 text-center shadow-sm">
          <p className={`text-sm font-bold uppercase ${theme.textMuted}`}>No published cards</p>
          <p className={`mx-auto mt-3 max-w-xl ${theme.textSecondary}`}>Publish cards first, then arrange them here.</p>
        </div>
      ) : (
        <div className="achievement-board-editor">
          <div className="achievement-public-board">
            {rows.map((row) => (
              <div
                key={`${row.shape}-${row.items.map((item) => item.id).join('-')}`}
                className={`achievement-public-row is-${row.shape} count-${row.items.length}`}
              >
                {row.items.map((item) => (
                  <EditorCard
                    key={item.id}
                    item={item}
                    isDragging={draggingId === item.id}
                    isTarget={targetId === item.id && draggingId !== item.id}
                    onDragStart={handleDragStart}
                    onDragEnter={handleDragEnter}
                    onDragEnd={handleDragEnd}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export default BoardEditorPage;
